import { Extension, ProctoringOption } from "@verity/proctoring-option/types";
import { CategoryOptionsListProps, OptionProps } from "../proctoringOptionsGrid";
import OptionBlock from "../optionBlock";
import { getUnsupportedToggleText, PROCTORING_OPTION_TEXT } from "../option-text";
import i18nPackage from "../../../i18n/i18n";
import { useState } from "react";
import { CheckBoxMenuSubItem } from "../option-menus/checkBoxMenuSubItem";
import { CoreButton } from "../../../core";
import ExtensionListModal from "../../edit-quiz-modals/extensionListModal";
import styles from "./index.module.css";

function RestrictHandoffOption<T extends Partial<ProctoringOption>>({
	option,
	updateOption,
	disabled,
	isOptionAllowed,
	horizontalOrientation,
	getPartiallySupportedPlatforms,
}: OptionProps<T>) {
	const { restrictHandoffMode } = option;
	const [bypassWasChecked, setBypassWasChecked] = useState<boolean>(restrictHandoffMode !== "strict");

	const notAllowed = !isOptionAllowed("restrictHandoffMode");
	return (
		<OptionBlock
			checked={restrictHandoffMode !== "off"}
			updateState={(checked) =>
				updateOption({
					...option,
					restrictHandoffMode: checked ? (bypassWasChecked ? "normal" : "strict") : "off",
				})
			}
			name="restrictHandoff"
			description={PROCTORING_OPTION_TEXT.restrictHandoffMode.settingDescription}
			Icon={PROCTORING_OPTION_TEXT.restrictHandoffMode.icon}
			contentHeader={PROCTORING_OPTION_TEXT.restrictHandoffMode.header}
			disabled={notAllowed}
			fullDisabled={disabled || notAllowed}
			disabledMessage={
				!notAllowed ? i18nPackage.t("editQuizPage.configurationStep.proctoringOptionsGridTSX.instituteDisabled") : ""
			}
			menuItems={
				isOptionAllowed("restrictHandoffMode", "normal") && isOptionAllowed("restrictHandoffMode", "strict") ? (
					<CheckBoxMenuSubItem
						label={i18nPackage.t("editQuizPage.configurationStep.proctoringOptionsGridTSX.bypassModeText")}
						checked={bypassWasChecked}
						onChange={(checked) => {
							updateOption({
								...option,
								restrictHandoffMode: checked ? "normal" : "strict",
							});
							setBypassWasChecked(checked);
						}}
						partiallySupportedPlatforms={getPartiallySupportedPlatforms?.("restrictHandoffMode", "strict")}
					/>
				) : undefined
			}
			horizontalOrientation={horizontalOrientation}
			partiallySupportedPlatforms={getPartiallySupportedPlatforms?.("restrictHandoffMode")}
		/>
	);
}

function DisableClipboardOption<T extends Partial<ProctoringOption>>({
	option,
	updateOption,
	disabled,
	isOptionAllowed,
	horizontalOrientation,
	getPartiallySupportedPlatforms,
}: OptionProps<T>) {
	const { disableClipboardMode } = option;
	const [systemWasChecked, setSystemWasChecked] = useState<boolean>(disableClipboardMode === "system");

	const notAllowed = !isOptionAllowed("disableClipboardMode");
	return (
		<OptionBlock
			name="disableClipboard"
			checked={disableClipboardMode !== "off"}
			updateState={(checked) =>
				updateOption({
					...option,
					disableClipboardMode: checked ? "tab" : "off",
				})
			}
			description={PROCTORING_OPTION_TEXT.disableClipboardMode.description}
			Icon={PROCTORING_OPTION_TEXT.disableClipboardMode.icon}
			contentHeader={PROCTORING_OPTION_TEXT.disableClipboardMode.header}
			disabled={notAllowed}
			fullDisabled={disabled || notAllowed}
			disabledMessage={
				notAllowed ? i18nPackage.t("editQuizPage.configurationStep.proctoringOptionsGridTSX.instituteDisabled") : ""
			}
			menuItems={
				isOptionAllowed("disableClipboardMode", "system") && (
					<CheckBoxMenuSubItem
						label={i18nPackage.t("editQuizPage.configurationStep.disableFeaturesTSX.disableSystemClipboard.header")}
						description={i18nPackage.t(
							"editQuizPage.configurationStep.disableFeaturesTSX.disableSystemClipboard.description",
						)}
						checked={systemWasChecked}
						onChange={(checked) => {
							updateOption({
								...option,
								disableClipboardMode: checked ? "system" : "tab",
							});
							setSystemWasChecked(checked);
						}}
						partiallySupportedPlatforms={getPartiallySupportedPlatforms?.("disableClipboardMode", "system")}
					/>
				)
			}
			horizontalOrientation={horizontalOrientation}
			partiallySupportedPlatforms={getPartiallySupportedPlatforms?.("disableClipboardMode")}
		/>
	);
}

function BlockExtensionsOption<T extends Partial<ProctoringOption>>({
	option,
	updateOption,
	disabled,
	isOptionAllowed,
	extensions,
	fetchChromeExtensionData,
	horizontalOrientation,
	getPartiallySupportedPlatforms,
}: OptionProps<T> & {
	extensions: Extension[];
	fetchChromeExtensionData: (id: string) => Promise<Extension | null>;
}) {
	const { extensionBlockMode, allowedExtensionsList } = option;
	const [bypassWasChecked, setBypassWasChecked] = useState<boolean>(extensionBlockMode !== "strict");
	const [allowListModalShowing, setAllowListModalShowing] = useState(false);

	const notAllowed = !isOptionAllowed("extensionBlockMode");
	return (
		<>
			<OptionBlock
				checked={extensionBlockMode !== "off"}
				updateState={(checked) => {
					updateOption({
						...option,
						extensionBlockMode: checked ? (bypassWasChecked ? "normal" : "strict") : "off",
						allowedExtensionsList: [],
					});
				}}
				name="extensionBlockMode"
				description={PROCTORING_OPTION_TEXT.extensionBlockMode.description}
				Icon={PROCTORING_OPTION_TEXT.extensionBlockMode.icon}
				contentHeader={PROCTORING_OPTION_TEXT.extensionBlockMode.header}
				disabled={notAllowed}
				fullDisabled={disabled || notAllowed}
				disabledMessage={i18nPackage.t("editQuizPage.configurationStep.proctoringOptionsGridTSX.instituteDisabled")}
				menuItems={
					<>
						<CoreButton
							sx={{ marginLeft: "10px", marginTop: "10px", marginBottom: "5px" }}
							onClick={() => setAllowListModalShowing(true)}
						>
							{i18nPackage.t("editQuizPage.configurationStep.disableFeaturesTSX.blockExtensions.addExtensions")}
						</CoreButton>
						{isOptionAllowed("extensionBlockMode", "normal") && isOptionAllowed("extensionBlockMode", "strict") ? (
							<CheckBoxMenuSubItem
								label={i18nPackage.t("editQuizPage.configurationStep.proctoringOptionsGridTSX.bypassModeText")}
								checked={bypassWasChecked}
								onChange={(checked) => {
									updateOption({
										...option,
										extensionBlockMode: checked ? "normal" : "strict",
									});
									setBypassWasChecked(checked);
								}}
							/>
						) : undefined}
					</>
				}
				horizontalOrientation={horizontalOrientation}
				partiallySupportedPlatforms={getPartiallySupportedPlatforms?.("extensionBlockMode")}
			/>
			{allowListModalShowing && (
				<ExtensionListModal
					onClose={() => setAllowListModalShowing(false)}
					allExtensions={extensions}
					initialSelectedExtensionNames={allowedExtensionsList ?? []}
					fetchChromeExtensionData={fetchChromeExtensionData}
					onSave={(extensionNames) =>
						updateOption({
							...option,
							allowedExtensionsList: extensionNames,
						})
					}
				/>
			)}
		</>
	);
}

function DisableVirtualCameraOption<T extends Partial<ProctoringOption>>({
	option,
	updateOption,
	disabled,
	isOptionAllowed,
	horizontalOrientation,
	getPartiallySupportedPlatforms,
}: OptionProps<T>) {
	const { disableVirtualCamera } = option;

	const notAllowed = !isOptionAllowed("disableVirtualCamera");
	return (
		<OptionBlock
			name="disableVirtualCamera"
			checked={Boolean(disableVirtualCamera)}
			updateState={(checked) =>
				updateOption({
					...option,
					disableVirtualCamera: checked,
				})
			}
			description={PROCTORING_OPTION_TEXT.disableVirtualCamera.description}
			Icon={PROCTORING_OPTION_TEXT.disableVirtualCamera.icon}
			contentHeader={PROCTORING_OPTION_TEXT.disableVirtualCamera.header}
			disabled={notAllowed}
			fullDisabled={disabled || notAllowed}
			disabledMessage={
				notAllowed ? i18nPackage.t("editQuizPage.configurationStep.proctoringOptionsGridTSX.instituteDisabled") : ""
			}
			horizontalOrientation={horizontalOrientation}
			partiallySupportedPlatforms={getPartiallySupportedPlatforms?.("disableVirtualCamera")}
		/>
	);
}

export default function DisableFeaturesOptions<T extends Partial<ProctoringOption>>({
	option,
	updateOption,
	disabled,
	shouldShowOption,
	isOptionAllowed,
	hasPassword = false,
	extensions,
	fetchChromeExtensionData,
	horizontalOrientation,
	getPartiallySupportedPlatforms,
}: CategoryOptionsListProps<T> & {
	extensions: Extension[];
	fetchChromeExtensionData: (id: string) => Promise<Extension | null>;
}) {
	const { disableSpellcheck, disableDownloadFiles, disablePrinting, disableRightClick, enableGuidedAccessMode } =
		option;

	const guidedAccessAllowed = isOptionAllowed("enableGuidedAccessMode");
	const disableDownloadAllowed = isOptionAllowed("disableDownloadFiles");
	const disableSpellcheckAllowed = isOptionAllowed("disableSpellcheck");
	const disablePrintingAllowed = isOptionAllowed("disablePrinting");
	const disableRightClickAllowed = isOptionAllowed("disableRightClick");
	return (
		<div className={styles.proctorSettingsGrid}>
			{shouldShowOption("disableVirtualCamera") && (
				<DisableVirtualCameraOption
					option={option}
					updateOption={updateOption}
					disabled={disabled}
					isOptionAllowed={isOptionAllowed}
					horizontalOrientation={horizontalOrientation}
					getPartiallySupportedPlatforms={getPartiallySupportedPlatforms}
				/>
			)}
			{shouldShowOption("disableClipboardMode") && (
				<DisableClipboardOption
					option={option}
					updateOption={updateOption}
					disabled={disabled}
					isOptionAllowed={isOptionAllowed}
					horizontalOrientation={horizontalOrientation}
					getPartiallySupportedPlatforms={getPartiallySupportedPlatforms}
				/>
			)}

			{shouldShowOption("enableGuidedAccessMode") && (
				<OptionBlock
					name="enableGuidedAccessMode"
					checked={Boolean(enableGuidedAccessMode)}
					updateState={(checked) =>
						updateOption({
							...option,
							enableGuidedAccessMode: checked,
						})
					}
					description={PROCTORING_OPTION_TEXT.enableGuidedAccessMode.description}
					Icon={PROCTORING_OPTION_TEXT.enableGuidedAccessMode.icon}
					contentHeader={PROCTORING_OPTION_TEXT.enableGuidedAccessMode.header}
					disabled={!guidedAccessAllowed}
					fullDisabled={!guidedAccessAllowed || disabled}
					disabledMessage={
						!guidedAccessAllowed
							? i18nPackage.t("editQuizPage.configurationStep.proctoringOptionsGridTSX.instituteDisabled")
							: getUnsupportedToggleText(
									option,
									"enableGuidedAccessMode",
									!enableGuidedAccessMode,
									Boolean(enableGuidedAccessMode),
								)
					}
					horizontalOrientation={horizontalOrientation}
					partiallySupportedPlatforms={getPartiallySupportedPlatforms?.("enableGuidedAccessMode")}
				/>
			)}

			{shouldShowOption("disableDownloadFiles") && (
				<OptionBlock
					name="disableDownloadFiles"
					checked={Boolean(disableDownloadFiles)}
					updateState={(checked) =>
						updateOption({
							...option,
							disableDownloadFiles: checked,
						})
					}
					description={PROCTORING_OPTION_TEXT.disableDownloadFiles.description}
					Icon={PROCTORING_OPTION_TEXT.disableDownloadFiles.icon}
					contentHeader={PROCTORING_OPTION_TEXT.disableDownloadFiles.header}
					disabled={!disableDownloadAllowed}
					fullDisabled={!disableDownloadAllowed || disabled}
					disabledMessage={
						!disableDownloadAllowed
							? i18nPackage.t("editQuizPage.configurationStep.proctoringOptionsGridTSX.instituteDisabled")
							: getUnsupportedToggleText(
									option,
									"disableDownloadFiles",
									!disableDownloadFiles,
									Boolean(disableDownloadFiles),
								)
					}
					horizontalOrientation={horizontalOrientation}
					partiallySupportedPlatforms={getPartiallySupportedPlatforms?.("disableDownloadFiles")}
				/>
			)}

			{shouldShowOption("disableSpellcheck") && (
				<OptionBlock
					name="disableSpellcheck"
					checked={Boolean(disableSpellcheck)}
					updateState={(checked) =>
						updateOption({
							...option,
							disableSpellcheck: checked,
						})
					}
					description={PROCTORING_OPTION_TEXT.disableSpellcheck.description}
					Icon={PROCTORING_OPTION_TEXT.disableSpellcheck.icon}
					contentHeader={PROCTORING_OPTION_TEXT.disableSpellcheck.header}
					disabled={!disableSpellcheckAllowed}
					fullDisabled={!isOptionAllowed("disableSpellcheck") || disabled}
					disabledMessage={
						!disableSpellcheckAllowed
							? i18nPackage.t("editQuizPage.configurationStep.proctoringOptionsGridTSX.instituteDisabled")
							: getUnsupportedToggleText(option, "disableSpellcheck", !disableSpellcheck, Boolean(disableSpellcheck))
					}
					horizontalOrientation={horizontalOrientation}
					partiallySupportedPlatforms={getPartiallySupportedPlatforms?.("disableSpellcheck")}
				/>
			)}

			{shouldShowOption("disablePrinting") && (
				<OptionBlock
					name="disablePrinting"
					checked={Boolean(disablePrinting)}
					updateState={(checked) =>
						updateOption({
							...option,
							disablePrinting: checked,
						})
					}
					description={PROCTORING_OPTION_TEXT.disablePrinting.description}
					Icon={PROCTORING_OPTION_TEXT.disablePrinting.icon}
					contentHeader={PROCTORING_OPTION_TEXT.disablePrinting.header}
					disabled={!disablePrintingAllowed}
					fullDisabled={!isOptionAllowed("disablePrinting") || disabled}
					disabledMessage={
						!disablePrintingAllowed
							? i18nPackage.t("editQuizPage.configurationStep.proctoringOptionsGridTSX.instituteDisabled")
							: getUnsupportedToggleText(option, "disablePrinting", !disablePrinting, Boolean(disablePrinting))
					}
					horizontalOrientation={horizontalOrientation}
					partiallySupportedPlatforms={getPartiallySupportedPlatforms?.("disablePrinting")}
				/>
			)}

			{shouldShowOption("disableRightClick") && (
				<OptionBlock
					name="disableRightClick"
					checked={Boolean(disableRightClick)}
					updateState={(checked) =>
						updateOption({
							...option,
							disableRightClick: checked,
						})
					}
					description={PROCTORING_OPTION_TEXT.disableRightClick.description}
					Icon={PROCTORING_OPTION_TEXT.disableRightClick.icon}
					contentHeader={PROCTORING_OPTION_TEXT.disableRightClick.header}
					disabled={!disableRightClickAllowed || (hasPassword && !option.disableRightClick)}
					fullDisabled={!isOptionAllowed("disableRightClick") || disabled}
					disabledMessage={
						!disableRightClickAllowed
							? i18nPackage.t("editQuizPage.configurationStep.proctoringOptionsGridTSX.instituteDisabled")
							: hasPassword
								? i18nPackage.t("editQuizPage.configurationStep.disableFeaturesTSX.rightClickPasswordConflictTxt")
								: getUnsupportedToggleText(option, "disableRightClick", !disableRightClick, Boolean(disableRightClick))
					}
					horizontalOrientation={horizontalOrientation}
					partiallySupportedPlatforms={getPartiallySupportedPlatforms?.("disableRightClick")}
				/>
			)}

			{shouldShowOption("extensionBlockMode") && (
				<BlockExtensionsOption
					option={option}
					updateOption={updateOption}
					disabled={disabled}
					isOptionAllowed={isOptionAllowed}
					extensions={extensions}
					fetchChromeExtensionData={fetchChromeExtensionData}
					horizontalOrientation={horizontalOrientation}
					getPartiallySupportedPlatforms={getPartiallySupportedPlatforms}
				/>
			)}

			{shouldShowOption("restrictHandoffMode") && (
				<RestrictHandoffOption
					option={option}
					updateOption={updateOption}
					disabled={disabled}
					isOptionAllowed={isOptionAllowed}
					horizontalOrientation={horizontalOrientation}
					getPartiallySupportedPlatforms={getPartiallySupportedPlatforms}
				/>
			)}
		</div>
	);
}
